import { useEffect, useLayoutEffect, useState, useRef } from 'react';
import { useElementHeight } from 'visitor-ui-component-library/utils/hooks/useElementHeight';
import styled from 'styled-components';
import { WHITE, DEFAULT_INPUT_BORDER_COLOR, DEFAULT_INPUT_BORDER_COLOR_DISABLED } from 'visitor-ui-component-library/theme/ColorConstants';
import { COMPOSER_INLINE_MARGIN, COMPOSER_BOTTOM_MARGIN } from '../widget-dimensions/constants/dimensions';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import { getDisclaimerKey } from './getDisclaimerKey';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SLIDE_UP_ANIMATION_DURATION = 0.5;
const FADE_IN_ANIMATION_DURATION = 1;
export const ANIMATION_DELAY = 1000;
const ComposerWrapper = styled.div.withConfig({
  displayName: "MessageComposer__ComposerWrapper"
})(["position:relative;min-height:", "px;"], ({
  $composerHeight
}) => $composerHeight + COMPOSER_BOTTOM_MARGIN);
const Composer = styled.div.withConfig({
  displayName: "MessageComposer__Composer"
})(["max-width:100%;background-color:", ";flex-shrink:0;transition:margin ", "s linear;margin:0 ", "px ", "px;border:1px solid ", ";border-radius:28px;padding:0 8px;[data-test-id='widget-textarea']{min-height:38px !important;}"], WHITE, SLIDE_UP_ANIMATION_DURATION, COMPOSER_INLINE_MARGIN, ({
  $showDisclaimer,
  $disclaimerHeight
}) => $showDisclaimer && $disclaimerHeight > 0 ? $disclaimerHeight + 5 : COMPOSER_BOTTOM_MARGIN, ({
  $disabled
}) => $disabled ? DEFAULT_INPUT_BORDER_COLOR_DISABLED : DEFAULT_INPUT_BORDER_COLOR);
const Inner = styled.div.withConfig({
  displayName: "MessageComposer__Inner"
})(["display:flex;width:100%;align-items:center;justify-content:space-between;flex-wrap:nowrap;gap:4px;"]);
const InputWrapper = styled.div.withConfig({
  displayName: "MessageComposer__InputWrapper"
})(["flex:1 1 0px;"]);
const ActionsWrapper = styled.div.withConfig({
  displayName: "MessageComposer__ActionsWrapper"
})(["display:flex;flex:0 0 auto;", ""], ({
  children
}) => !children && 'display: none;');
const Disclaimer = styled.div.withConfig({
  displayName: "MessageComposer__Disclaimer"
})(["font-size:0.9em;text-align:center;opacity:", ";width:100%;transition:opacity ", "s ease-in;position:absolute;padding:0 20px;bottom:3px;"], ({
  $isVisible
}) => $isVisible ? '1' : '0', FADE_IN_ANIMATION_DURATION);
export default function MessageComposer({
  FileUploads = null,
  inputToRender,
  children,
  disabled,
  enableAIDisclaimer = false,
  spamProtectionEnabled
}) {
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [disclaimerHeight, setDisclaimerHeight] = useState(0);
  const disclaimerRef = useRef(null);
  const composerRef = useRef(null);
  const composerHeight = useElementHeight(composerRef);
  useEffect(() => {
    if (enableAIDisclaimer || spamProtectionEnabled) {
      const timer = setTimeout(() => {
        setShowDisclaimer(true);
      }, ANIMATION_DELAY);
      return () => clearTimeout(timer);
    }
  }, [enableAIDisclaimer, spamProtectionEnabled]);
  const disclaimerKey = getDisclaimerKey(enableAIDisclaimer, spamProtectionEnabled);
  useLayoutEffect(() => {
    if (showDisclaimer && disclaimerRef.current) {
      const height = disclaimerRef.current.offsetHeight;
      setDisclaimerHeight(height);
    }
  }, [showDisclaimer, disclaimerKey]);
  return /*#__PURE__*/_jsxs(ComposerWrapper, {
    showDisclaimer: showDisclaimer,
    $disclaimerHeight: disclaimerHeight,
    $composerHeight: composerHeight,
    "data-test-id": "composer-wrapper",
    children: [/*#__PURE__*/_jsxs(Composer, {
      $disabled: disabled,
      $showDisclaimer: showDisclaimer,
      $disclaimerHeight: disclaimerHeight,
      ref: composerRef,
      children: [FileUploads, /*#__PURE__*/_jsxs(Inner, {
        children: [/*#__PURE__*/_jsx(InputWrapper, {
          children: inputToRender
        }), /*#__PURE__*/_jsx(ActionsWrapper, {
          children: children
        })]
      })]
    }), disclaimerKey && /*#__PURE__*/_jsx(Disclaimer, Object.assign({
      ref: disclaimerRef,
      $isVisible: showDisclaimer
    }, showDisclaimer && {
      /* eslint-disable-next-line conversations/no-dynamic-i18n */
      'aria-label': I18n.text(disclaimerKey)
    }, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: disclaimerKey
      })
    }))]
  });
}
MessageComposer.displayName = 'MessageComposer';